import * as React from 'react';
import { observer, Provider } from 'mobx-react';
import Transition, { TransitionProps } from 'react-transition-group/Transition';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import * as PageGive from './page-give/page-give';
import * as PageEnterMobileNumber from './page-enter-mobile-number/page-enter-mobile-number';
import * as PageConfirmMobileNumber from './page-confirm-mobile-number/page-confirm-mobile-number';
import * as PageCreateAccount from './page-create-account/page-create-account';
import * as PagePaymentMethod from './page-payment-method/page-payment-method';
import * as PageConfirmBankAccount from './page-confirm-bank-account/page-confirm-bank-account';
import * as PageConfirmGift from './page-confirm-gift/page-confirm-gift';
import * as PagePaymentSuccess from './page-payment-success/page-payment-success';
import { ValidationState } from '../validation/view-model-validator';
import { WebGivingError } from '../components/error-message/error-message';

import { transitionDurationLongMs } from '../main/webgiving-main.less';
import { isFunction } from '../../Shared/utils/is-function';
import { getInjectablePropsForViewProvider } from '../utils/injectable-props';
import { ErrorInfo, getErrorInfo } from '../utils/error-utils';
import { PageError } from './page-error';

const pageTransitionDuration = parseInt(transitionDurationLongMs, 10);

export enum WebGivingPage {
	Give,
	EnterMobileNumber,
	ConfirmMobileNumber,
	CreateAccount,
	PaymentMethod,
	ConfirmBankAccount,
	ConfirmGift,
	ConfirmMobileNumberForPayment,
	PaymentSuccess,
}

export namespace WebGivingPageView {
	export import Give = PageGive.PageGive;
	export import EnterMobileNumber = PageEnterMobileNumber.PageEnterMobileNumber;
	export import ConfirmMobileNumber = PageConfirmMobileNumber.PageConfirmMobileNumber;
	export import CreateAccount = PageCreateAccount.PageCreateAccount;
	export import PaymentMethod = PagePaymentMethod.PagePaymentMethod;
	export import ConfirmBankAccount = PageConfirmBankAccount.PageConfirmBankAccount;
	export import ConfirmGift = PageConfirmGift.PageConfirmGift;
	export import PaymentSuccess = PagePaymentSuccess.PagePaymentSuccess;
}

export type WebGivingPageView = typeof WebGivingPageView[keyof typeof WebGivingPageView]['prototype'];

export namespace WebGivingPageViewModel {
	export import Give = PageGive.PageGiveViewModel;
	export import EnterMobileNumber = PageEnterMobileNumber.PageEnterMobileNumberViewModel;
	export import ConfirmMobileNumber = PageConfirmMobileNumber.PageConfirmMobileNumberViewModel;
	export import CreateAccount = PageCreateAccount.PageCreateAccountViewModel;
	export import PaymentMethod = PagePaymentMethod.PagePaymentMethodViewModel;
	export import ConfirmBankAccount = PageConfirmBankAccount.PageConfirmBankAccountViewModel;
	export import ConfirmGift = PageConfirmGift.PageConfirmGiftViewModel;
	export import PaymentSuccess = PagePaymentSuccess.PagePaymentSuccessViewModel;
}

export type WebGivingPageViewModel = typeof WebGivingPageViewModel[keyof typeof WebGivingPageViewModel]['prototype'];

export interface UserActionState {
	blocked?: boolean;
}

export interface IWebGivingPageViewModel {
	readonly type: WebGivingPage;
	isLoading: boolean;
	isBlocked: boolean;
	error: WebGivingError;
	pageShouldBeVisible: boolean;
	userActionState: UserActionState;
	validate(): ValidationState;
	setPageShouldBeVisible(): void;
	blockUserActions(): void;
}

export interface IRouterProps {
	currentPage: WebGivingPageViewModel;
	setCurrentPageShouldBeVisible: () => void;
	onError: (errorInfo: ErrorInfo, customData?: any) => void;
}

@observer
export class Router extends React.Component<IRouterProps, { hasPageError: boolean }> {

	constructor(props: any) {
		super(props);
		this.state = { hasPageError: false };
	}

	UNSAFE_componentWillReceiveProps(nextProps: IRouterProps) {
		if (!nextProps.currentPage.error) {
			this.setState({ hasPageError: false });
		}
	}

	componentDidCatch(error: any, reactErrorInfo: React.ErrorInfo) {
		this.props.setCurrentPageShouldBeVisible();
		window.scroll(0, 0);

		const errorInfo = getErrorInfo(error);

		this.setState({ hasPageError: true });
		this.props.onError(errorInfo, reactErrorInfo);
	}

	render() {
		const { currentPage, setCurrentPageShouldBeVisible } = this.props;

		if (this.state.hasPageError) {
			return (
				<TransitionGroup>
					<ViewTransition key="page-error" setCurrentPageShouldBeVisible={setCurrentPageShouldBeVisible} vm={currentPage} view={PageError} />
				</TransitionGroup>
			);
		}

		const [viewModelKey] = Object.keys(WebGivingPageViewModel).filter(x => currentPage instanceof (WebGivingPageViewModel as any)[x]);
		if (!viewModelKey) {
			throw new Error('Current viewModel is not supported!');
		}

		const view = (WebGivingPageView as any)[viewModelKey];
		if (!view) {
			throw new Error(`View for viewModel '${viewModelKey}' does not exists.`);
		}

		return (
			<TransitionGroup>
				<ViewTransition key={viewModelKey} setCurrentPageShouldBeVisible={setCurrentPageShouldBeVisible} vm={currentPage} view={view} />
			</TransitionGroup>
		);
	}
}

class ViewTransition extends React.Component<{
	vm: WebGivingPageViewModel;
	view: any;
	setCurrentPageShouldBeVisible: () => void;
} & Partial<TransitionProps>> {
	render() {
		const { vm, view, setCurrentPageShouldBeVisible, ...rest } = this.props;

		return (
			<Provider {...getInjectablePropsForViewProvider(vm.userActionState) }>
				<Transition  {...rest} timeout={pageTransitionDuration} onExit={this.onExit} onExited={this.onExited}>
					{(state: any) => React.createElement(view, {
						vm,
						transitionState: state,
					})}
				</Transition>
			</Provider>
		);
	}

	private onExit = (node: HTMLElement) => {
		const { onExit, vm } = this.props;
		if (isFunction(onExit)) {
			onExit(node);
		}

		// disable user action channel on the current vm
		vm.blockUserActions();
	}

	private onExited = (node: HTMLElement) => {
		const { setCurrentPageShouldBeVisible, onExited } = this.props;
		if (isFunction(onExited)) {
			onExited(node);
		}
		setCurrentPageShouldBeVisible();
		window.scroll(0, 0);
	}
}
